// frontend/src/modules/admin/components/GestionHRUsers.jsx
// Gestion des comptes administrateurs (HR_users) avec 2FA
// 73+ Papy - Décembre 2025

import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Key, 
  Save, 
  Plus, 
  Trash2, 
  Eye, 
  EyeOff, 
  Shield, 
  UserCog,
  RefreshCw,
  Check,
  X,
  AlertTriangle,
  Smartphone,
  QrCode,
  Lock,
  Unlock
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// Icônes pour les rôles
const roleIcons = {
  superviseur: <Shield className="h-5 w-5 text-purple-600" />,
  gestionnaire: <UserCog className="h-5 w-5 text-blue-600" />,
  admin: <Shield className="h-5 w-5 text-red-600" />
};

const roleLabels = {
  superviseur: 'Superviseur',
  gestionnaire: 'Gestionnaire',
  admin: 'Admin (legacy)'
};

const roleBadgeColors = {
  superviseur: 'bg-purple-100 text-purple-800',
  gestionnaire: 'bg-blue-100 text-blue-800',
  admin: 'bg-red-100 text-red-800'
};

const GestionHRUsers = () => {
  // États
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // État pour le formulaire de nouveau compte
  const [showNewForm, setShowNewForm] = useState(false);
  const [newUser, setNewUser] = useState({
    login: '',
    password: '',
    role: 'gestionnaire',
    nom_complet: '',
    email: ''
  });
  
  // État pour modifier un mot de passe
  const [editingPasswordId, setEditingPasswordId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState({});
  
  // États 2FA
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [selected2FAUser, setSelected2FAUser] = useState(null);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpStep, setTotpStep] = useState('setup'); // 'setup', 'verify', 'disable'
  
  // Utilisateur connecté (pour vérifier si superviseur)
  const [currentUser, setCurrentUser] = useState(null);
  
  // Charger l'utilisateur connecté
  useEffect(() => {
    const storedUser = localStorage.getItem('farm_admin_user');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Erreur parsing user:', e);
      }
    }
  }, []);
  
  // Vérifier si l'utilisateur actuel est superviseur
  const isSuperviseur = currentUser?.role === 'superviseur';
  
  // Charger les utilisateurs
  const loadUsers = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE}/hr-users`);
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data);
      } else {
        setError(data.error || 'Erreur de chargement');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
      console.error('Erreur loadUsers:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadUsers();
  }, []);
  
  // Effacer les messages après 3 secondes
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);
  
  // Créer un nouveau compte
  const handleCreateUser = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!newUser.login || !newUser.password) {
      setError('Login et mot de passe requis');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/hr-users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newUser)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Compte "${newUser.login}" créé avec succès !`);
        setNewUser({ login: '', password: '', role: 'gestionnaire', nom_complet: '', email: '' });
        setShowNewForm(false);
        loadUsers();
      } else {
        setError(data.error || 'Erreur lors de la création');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
  };
  
  // Modifier le mot de passe
  const handleUpdatePassword = async (id) => {
    if (!newPassword) {
      setError('Mot de passe requis');
      return;
    }
    
    if (newPassword.length < 4) {
      setError('Mot de passe trop court (minimum 4 caractères)');
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/hr-users/${id}/password`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess('Mot de passe mis à jour !');
        setEditingPasswordId(null);
        setNewPassword('');
      } else {
        setError(data.error || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
  };
  
  // Activer/Désactiver un compte
  const handleToggleActive = async (id, currentActive) => {
    try {
      const response = await fetch(`${API_BASE}/hr-users/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ actif: currentActive ? 0 : 1 })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Compte ${currentActive ? 'désactivé' : 'activé'} !`);
        loadUsers();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
  };
  
  // Supprimer un compte
  const handleDelete = async (id, login) => {
    if (!window.confirm(`Supprimer le compte "${login}" ? Cette action est irréversible.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/hr-users/${id}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`Compte "${login}" supprimé`);
        loadUsers();
      } else {
        setError(data.error);
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    }
  };
  
  // Toggle affichage mot de passe
  const toggleShowPassword = (id) => {
    setShowPassword(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ==========================================
  // FONCTIONS 2FA
  // ==========================================
  
  // Ouvrir le modal 2FA pour configurer
  const open2FASetup = async (user) => {
    setSelected2FAUser(user);
    setTotpCode('');
    setQrCodeData(null);
    setTotpLoading(true);
    setShow2FAModal(true);
    
    if (user.totp_enabled) {
      // Déjà activé → proposer de désactiver
      setTotpStep('disable');
      setTotpLoading(false);
    } else {
      // Pas activé → générer QR code
      setTotpStep('setup');
      
      try {
        const response = await fetch(`${API_BASE}/totp/setup/${user.id}`);
        const data = await response.json();
        
        if (data.success) {
          setQrCodeData(data);
        } else {
          setError(data.error || 'Erreur génération QR code');
          setShow2FAModal(false);
        }
      } catch (err) {
        setError('Erreur de connexion au serveur');
        setShow2FAModal(false);
      } finally {
        setTotpLoading(false);
      }
    }
  };
  
  // Vérifier et activer le 2FA
  const verify2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Code à 6 chiffres requis');
      return;
    }
    
    setTotpLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/totp/verify-setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selected2FAUser.id,
          token: totpCode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`2FA activé pour ${selected2FAUser.login} !`);
        setShow2FAModal(false);
        loadUsers();
      } else {
        setError(data.error || 'Code incorrect');
        setTotpCode('');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setTotpLoading(false);
    }
  };
  
  // Désactiver le 2FA
  const disable2FA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Entrez le code actuel pour confirmer');
      return;
    }
    
    setTotpLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/totp/disable`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: selected2FAUser.id,
          token: totpCode
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setSuccess(`2FA désactivé pour ${selected2FAUser.login}`);
        setShow2FAModal(false);
        loadUsers();
      } else {
        setError(data.error || 'Code incorrect');
        setTotpCode('');
      }
    } catch (err) {
      setError('Erreur de connexion au serveur');
    } finally {
      setTotpLoading(false);
    }
  };
  
  // Fermer le modal 2FA
  const close2FAModal = () => {
    setShow2FAModal(false);
    setSelected2FAUser(null);
    setQrCodeData(null);
    setTotpCode('');
    setTotpStep('setup');
  };
  
  // Gérer la saisie du code TOTP
  const handleTotpChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setTotpCode(value);
    setError('');
  };

  // ==========================================
  // RENDU
  // ==========================================
  
  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Users className="h-8 w-8 text-gray-700" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Gestion des comptes</h2>
            <p className="text-gray-600">Administrateurs et gestionnaires</p>
          </div>
        </div>
        
        <button
          onClick={() => setShowNewForm(!showNewForm)}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Nouveau compte
        </button>
      </div>
      
      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0" />
          <p className="text-red-800">{error}</p>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}
      
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <Check className="h-5 w-5 text-green-600" />
          <p className="text-green-800">{success}</p>
        </div>
      )}
      
      {/* Formulaire nouveau compte */}
      {showNewForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            Créer un nouveau compte
          </h3>
          
          <form onSubmit={handleCreateUser}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Login *
                </label>
                <input
                  type="text"
                  value={newUser.login}
                  onChange={(e) => setNewUser({ ...newUser, login: e.target.value })}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="identifiant"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe *
                </label>
                <div className="relative">
                  <input
                    type={showPassword['new'] ? 'text' : 'password'}
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 pr-10"
                    placeholder="••••••••"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => toggleShowPassword('new')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword['new'] ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rôle
                </label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
                >
                  <option value="gestionnaire">Gestionnaire</option>
                  <option value="superviseur">Superviseur</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom complet
                </label>
                <input
                  type="text"
                  value={newUser.nom_complet}
                  onChange={(e) => setNewUser({ ...newUser, nom_complet: e.target.value })}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="Jean Dupont"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500"
                  placeholder="jean@ferme.fr"
                />
              </div>
            </div>
            
            <div className="flex gap-3 pt-4">
              <button
                type="submit"
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Save className="h-5 w-5" />
                Créer le compte
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewForm(false);
                  setNewUser({ login: '', password: '', role: 'gestionnaire', nom_complet: '', email: '' });
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* Liste des comptes */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b bg-gray-50">
          <h3 className="text-lg font-semibold text-gray-900">
            Comptes existants ({users.length})
          </h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center text-gray-500">
            <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2" />
            Chargement...
          </div>
        ) : users.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            Aucun compte trouvé
          </div>
        ) : (
          <div className="divide-y">
            {users.map((user) => (
              <div 
                key={user.id} 
                className={`p-4 ${!user.actif ? 'bg-gray-100 opacity-60' : 'hover:bg-gray-50'}`}
              >
                <div className="flex items-center justify-between">
                  {/* Info utilisateur */}
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-gray-100 rounded-full">
                      {roleIcons[user.role] || <Users className="h-5 w-5 text-gray-600" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{user.login}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${roleBadgeColors[user.role]}`}>
                          {roleLabels[user.role]}
                        </span>
                        {user.totp_enabled === 1 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-800 flex items-center gap-1">
                            <Lock className="h-3 w-3" />
                            2FA
                          </span>
                        )}
                        {!user.actif && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-800">
                            Inactif
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">
                        {user.nom_complet || 'Sans nom'} 
                        {user.email && <span className="ml-2">• {user.email}</span>}
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {/* Modifier mot de passe */}
                    {editingPasswordId === user.id ? (
                      <div className="flex items-center gap-2">
                        <div className="relative">
                          <input
                            type={showPassword[user.id] ? 'text' : 'password'}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Nouveau mot de passe"
                            className="w-40 p-2 text-sm border rounded-md pr-8"
                            autoFocus
                          />
                          <button
                            type="button"
                            onClick={() => toggleShowPassword(user.id)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400"
                          >
                            {showPassword[user.id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <button
                          onClick={() => handleUpdatePassword(user.id)}
                          className="p-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                          title="Valider"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingPasswordId(null);
                            setNewPassword('');
                          }}
                          className="p-2 bg-gray-200 text-gray-600 rounded-md hover:bg-gray-300"
                          title="Annuler"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <button
                          onClick={() => setEditingPasswordId(user.id)}
                          className="p-2 bg-blue-100 text-blue-600 rounded-md hover:bg-blue-200 transition-colors"
                          title="Modifier le mot de passe"
                        >
                          <Key className="h-4 w-4" />
                        </button>
                        
                        {/* Bouton 2FA - Visible uniquement pour superviseur */}
                        {isSuperviseur && (
                          <button
                            onClick={() => open2FASetup(user)}
                            className={`p-2 rounded-md transition-colors ${
                              user.totp_enabled 
                                ? 'bg-green-100 text-green-600 hover:bg-green-200' 
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                            title={user.totp_enabled ? 'Gérer 2FA' : 'Activer 2FA'}
                          >
                            <Smartphone className="h-4 w-4" />
                          </button>
                        )}
                        
                        <button
                          onClick={() => handleToggleActive(user.id, user.actif)}
                          className={`p-2 rounded-md transition-colors ${
                            user.actif 
                              ? 'bg-orange-100 text-orange-600 hover:bg-orange-200' 
                              : 'bg-green-100 text-green-600 hover:bg-green-200'
                          }`}
                          title={user.actif ? 'Désactiver' : 'Activer'}
                        >
                          {user.actif ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        
                        <button
                          onClick={() => handleDelete(user.id, user.login)}
                          className="p-2 bg-red-100 text-red-600 rounded-md hover:bg-red-200 transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">💡 Rôles et sécurité</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li><strong>Superviseur</strong> — Accès complet + gestion 2FA</li>
          <li><strong>Gestionnaire</strong> — Accès aux réservations, cultures, stats</li>
          <li><strong>🔐 2FA</strong> — Authentification à deux facteurs avec Google Authenticator</li>
        </ul>
      </div>
      
      {/* Modal 2FA */}
      {show2FAModal && selected2FAUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-full">
                  <Smartphone className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {totpStep === 'disable' ? 'Désactiver 2FA' : 'Configurer 2FA'}
                  </h3>
                  <p className="text-sm text-gray-500">{selected2FAUser.login}</p>
                </div>
              </div>
              <button
                onClick={close2FAModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {/* Contenu */}
            {totpLoading ? (
              <div className="py-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-green-600" />
                <p className="text-gray-500">Chargement...</p>
              </div>
            ) : totpStep === 'setup' && qrCodeData ? (
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  1. Scannez ce QR code avec Google Authenticator
                </p>
                
                {/* QR Code */}
                <div className="flex justify-center">
                  <img 
                    src={qrCodeData.qrCode} 
                    alt="QR Code 2FA" 
                    className="w-48 h-48 border rounded-lg"
                  />
                </div>
                
                {/* Secret pour saisie manuelle */}
                <div className="bg-gray-100 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Ou saisissez ce code manuellement :</p>
                  <p className="font-mono text-sm text-gray-800 break-all select-all">
                    {qrCodeData.secret}
                  </p>
                </div>
                
                <p className="text-sm text-gray-600">
                  2. Entrez le code à 6 chiffres affiché dans l'app
                </p>
                
                {/* Input code */}
                <input
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={handleTotpChange}
                  placeholder="000000"
                  className="w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-green-500"
                  maxLength={6}
                  autoFocus
                />
                
                {/* Erreur */}
                {error && (
                  <p className="text-sm text-red-600 text-center">{error}</p>
                )}
                
                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    onClick={verify2FA}
                    disabled={totpCode.length !== 6 || totpLoading}
                    className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium"
                  >
                    Activer 2FA
                  </button>
                  <button
                    onClick={close2FAModal}
                    className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : totpStep === 'disable' ? (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-yellow-800 font-medium">
                        Attention : Désactiver le 2FA réduit la sécurité du compte
                      </p>
                      <p className="text-xs text-yellow-700 mt-1">
                        Entrez le code actuel de Google Authenticator pour confirmer
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Input code */}
                <input
                  type="text"
                  inputMode="numeric"
                  value={totpCode}
                  onChange={handleTotpChange}
                  placeholder="000000"
                  className="w-full px-4 py-3 border rounded-lg text-center text-2xl font-mono tracking-widest focus:ring-2 focus:ring-red-500"
                  maxLength={6}
                  autoFocus
                />
                
                {/* Erreur */}
                {error && (
                  <p className="text-sm text-red-600 text-center">{error}</p>
                )}
                
                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    onClick={disable2FA}
                    disabled={totpCode.length !== 6 || totpLoading}
                    className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium"
                  >
                    <Unlock className="h-4 w-4 inline mr-2" />
                    Désactiver 2FA
                  </button>
                  <button
                    onClick={close2FAModal}
                    className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            ) : (
              <div className="py-8 text-center text-gray-500">
                Erreur de chargement
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionHRUsers;